import { request } from './base';
import { snakify } from '@/helpers/common';
import { BONUSES_QUANTITY, FREESPINS_QUANTITY } from '@/helpers/constants';
import { Levels } from '@/helpers/jackpot';
import { License } from '@/store/commonStore/types';

export interface Currency {
  code: string;
  symbol: string;
  subunitsToUnit: number;
  fiat: boolean;
  subcurrencies: {
    code: string;
    symbol: string;
    subunitsToUnit: number;
  }[];
}

export const currencies = () => request<Currency[]>({ url: 'info/currencies' });

export interface ICountry {
  name: string;
  code: string;
  defaultCurrency: string;
}

export const countries = () => request<ICountry[]>({ url: 'info/countries' });

export interface PlayerFields {
  contexts: { [key: string]: string[] };
  fields: {
    field: string;
    type: string;
    length?: {
      minimum?: number;
      maximum?: number;
    };
    onceSet?: boolean;
    age?: any;
    inclusion?: any;
    service?: string;
  }[];
}

export const playerFields = (playerState?: string) =>
  request<PlayerFields>({
    url: `info/player_fields${playerState ? `?player_country=${playerState}` : ''}`,
  });

export interface PlayerSettings {
  cent: {
    authEndpoint: string;
    timestamp: string;
    token: string;
    url: string;
    user: string;
  };
}

export const playerSettings = () =>
  request<PlayerSettings>({ url: 'player/settings' });

export interface PlayerTransaction {
  id: number;
  amountCents: number;
  currency: string;
  action: string;
  paymentSystem: string;
  recallable: boolean;
  createdAt: string;
  finishedAt: string;
  systemName?: string;
  state?: string;
  brand?: string;
  success: boolean;
}

export const playerTransactions = () =>
  request<PlayerTransaction[]>({ url: 'player/payments' });

interface Locale {
  code: string;
  default: boolean;
  name: string;
  nameInLocale: string;
}

export const pushStag = (params: object) =>
  request<Locale[]>({ url: 'info/locales', data: snakify(params) });

export interface CurrentIp {
  countryCode: string;
  ip: string;
  regionCode: string;
  defaultCurrency: string;
}

export const getCurrentIp = () => request<CurrentIp>({ url: 'current_ip' });

interface ResponseLocale {
  locale: string;
}

export const updateLocale = (params: ResponseLocale) =>
  request<ResponseLocale>({
    url: 'player/update_locale',
    method: 'PATCH',
    data: snakify(params),
  });

export enum BonusStage {
  Issued = 'issued',
  Expired = 'expired',
  HandleBets = 'handle_bets',
  Canceled = 'canceled',
  WagerDone = 'wager_done',
  Activated = 'activated',
  Finished = 'finished',
  Lost = 'lost',
}

export enum LootboxStage {
  Issued = 'issued',
  Expired = 'expired',
  Lost = 'lost',
  Canceled = 'canceled',
  Activated = 'activated',
}

export const bonusIdBaseMap = {
  [BonusStage.Issued]: 1e6,
  [BonusStage.HandleBets]: 2e6,
  [BonusStage.WagerDone]: 3e6,
  [BonusStage.Canceled]: 4e6,
  [BonusStage.Expired]: 4e6,
};

export const lootboxIdBaseMap = {
  [LootboxStage.Issued]: 5e6,
  [LootboxStage.Activated]: 6e6,
  [LootboxStage.Lost]: 7e6,
  [LootboxStage.Canceled]: 7e6,
  [LootboxStage.Expired]: 7e6,
};

enum BonusStrategy {
  Deposit = 'deposit',
  Exchange = 'exchange',
  FreespinsResult = 'freespins_result',
  GroupsUpdated = 'groups_updated',
  InputCoupon = 'input_coupon',
  JackpotAward = 'jackpot_award',
  Manual = 'manual',
  Prize = 'prize',
  PrizeAward = 'prize_award',
  Registration = 'registration',
  Scheduler = 'scheduler',
}

export interface BonusStageI {
  stage: BonusStage;
}

export interface Bonus extends BonusStageI {
  id: number;
  title: string;
  amountCents: number;
  amountWagerCents: number;
  amountWagerRequirementCents: number;
  currency: string;
  strategy: BonusStrategy;
  createdAt: string;
  validUntil: string;
  activatableUntil: string | null;
  cancelable: true;
  activatable: true;
  gamesInfo: {
    identifier: string;
    title: string;
    frontendUrl: string;
  }[];
  games: string[];
  jackpot?: {
    gameIdentifier: string;
    name: string;
    levelName: string;
    levelIndex: number;
    jackpotExternal_code: string;
    jackpotLevelExternalCode: string;
    winMessage: string;
  };
  groupKey: string;
}

export const playerBonuses = () =>
  request<Bonus[]>({
    url: 'player/bonuses',
    data: { quantity: BONUSES_QUANTITY },
  });

export interface Freespin extends BonusStageI {
  id: number;
  title: string;
  freespinsTotal: number;
  freespinsPerformed: number | null;
  games: string[];
  activationPath: string;
  provider: string;
  activatableUntil: string | null;
  validUntil: string | null;
  activatable: boolean;
  activationCondition: null;
  cancelable: boolean;
  createdAt: string;
  betLevel: number;
  currency: string;
  ceCurrency: string | null;
  groupKey?: string;
  gamesInfo?: {
    identifier: string;
    title: string;
    frontendUrl: string;
  }[];
}

export const playerFreespins = () =>
  request<Freespin[]>({
    url: 'player/freespins',
    data: { quantity: FREESPINS_QUANTITY },
  });

export interface WinnerFields {
  game: {
    identifier: string;
    table_image_path: string;
    table_url: string;
    title: string;
  };
  player: {
    nickname: string;
  };
  round: {
    bet?: number;
    currency?: string;
    win: number;
  };
}

interface BonusAttribute {
  field: string;
  value: number | { currency: string; amountCents: number }[];
}

export interface LootboxBonus {
  title: string;
  type: string;
  attributes: BonusAttribute[];
  resultBonus?: BonusAttribute[];
}

export interface LootboxItem {
  id: number;
  weight: number;
  prize: boolean;
  createdAt: string;
  bonuses: LootboxBonus[];
}

export interface Lootbox {
  id: number;
  title: string;
  stage: LootboxStage;
  strategy: string;
  createdAt: string;
  validUntil: string | null;
  display: number;
  items: LootboxItem[];
  live?: boolean;
  groupKey: string;
}

export const playerLootboxes = () =>
  request<Lootbox[]>({ url: 'player/lootboxes' });

export const playerLootboxActivation = (id: number) =>
  request<LootboxItem>({
    method: 'POST',
    url: `player/lootboxes/${id}/activation`,
  });

export interface PlayerJackpotWin {
  jackpotWinId: number;
  reason: string;
  id: number;
  strategy: string;
  createdAt: string;
  activatable: boolean;
  activatableUntil: string;
  amountCents: number;
  amountWager_cents: number;
  amountWagerRequirementCents: number;
  cancelable: boolean;
  title: string;
  validUntil: string;
  stage: string;
  currency: string;
  groupKey: string;
  jackpot: {
    gameIdentifier: string;
    levelIndex: number;
    levelName: string;
    name: string;
    jackpotExternalCode: string;
    jackpotLevelExternalCode: string;
    winMessage: string;
  };
}

export interface Jackpot {
  allowedCurrencies: string[];
  currency: string;
  externalId: string;
  games: string[];
  id: number;
  identifier: string;
  name: string;
  state: string;
  levels: JackpotLevel[];
}

export interface JackpotLevel {
  amountCents: number;
  availableAmountCents: number;
  currentAwardValue: number | null;
  id: number;
  identifier: string;
  index: number;
  name: string;
  periods: JackpotPeriod[];
  state: string;
  totalAmountCents: number;
  workingDays: string[];
}

export interface JackpotPeriod {
  startDate: string;
  endDate: string;
}

export enum JackpotStage {
  Created = 'created',
  Active = 'active',
  Disabled = 'disabled',
  Stopped = 'stopped',
  Finished = 'finished',
}

export interface Rakebacks {
  currency: string;
  amountCents: number;
  amountCentsDecimal: string;
}

export const jackpots = () => request<Jackpot[]>({ url: 'jackpots' });

export interface JackpotWinnner {
  jackpotName: string;
  jackpotLevelName: string;
  jackpotExternalCode: string;
  jackpotLevelExternalCode: Levels;
  userName: string;
  createdAt: string;
  approvedAt: string;
  amountCents: number;
  jackpotOriginalCurrency: string;
  jackpotOriginalAmountCents: number;
  gameIdentifier: string;
  gameProvider: string;
}

export const jackpotWinnersList = () =>
  request<JackpotWinnner[]>({ url: 'jackpot_wins?per_page=100' });

export enum BonusAttributeField {
  Wager = 'wager',
  BonusAmount = 'bonus_amount',
  FreespinsMaxWin = 'freespins_max_win',
  MaxWin = 'max_win',
  FreespinsCount = 'freespins_count',
}

export const playerBonusesActivation = (id: number) =>
  request({
    method: 'POST',
    url: `player/bonuses/${id}/activation`,
  });

export const playerFreespinsActivation = (id: number) =>
  request({
    method: 'POST',
    url: `player/freespins/${id}/activation`,
  });

export const playerBonusesCancellation = (id: number) =>
  request({
    method: 'DELETE',
    url: `player/bonuses/${id}`,
  });

export const playerFreespinsCancellation = (id: number) =>
  request({
    method: 'DELETE',
    url: `player/freespins/${id}`,
  });

export const infoProject = () =>
  request<{ license: License; availableReferralSystem: boolean }>({
    url: 'info/project',
  });

export const sumsubAccessToken = () =>
  request<{ accessToken: string }>({
    url: 'sumsub/access_token',
  });

export const playerRakebacks = () =>
  request<Rakebacks[]>({ url: 'player/rakebacks' });
